/**
 * HustleCRMSalesPage.jsx — sales page for HustleCRM
 * (the "GoHighLevel for HustleSuite" — every lead, review, booking,
 * DM, offer-claim in one inbox). Bare-domain hustlecrm.au.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, Printer, Sparkles, Inbox, MessageSquare,
  Zap, Users, Bell, ShieldCheck, TrendingUp, BadgeCheck,
  Workflow, Building2,
} from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import FounderChatPill from "@/components/FounderChatPill";

const LOGO = "https://customer-assets.emergentagent.com/job_mobile-resto/artifacts/d6gkfgwo_Local%20Hustle%20Logo%20-%20PLAZA%20LIGHT.png";

const ctaPrimary = {
  display: "inline-flex", alignItems: "center", gap: 8,
  padding: "12px 22px", borderRadius: 999, border: "none",
  background: "#c2410c", color: "#fff", fontWeight: 700, fontSize: 15,
  cursor: "pointer", boxShadow: "0 8px 20px rgba(194,65,12,.22)",
  textDecoration: "none",
};
const ctaSecondary = {
  display: "inline-flex", alignItems: "center", gap: 8,
  padding: "12px 22px", borderRadius: 999,
  border: "1.5px solid #1a1408", color: "#1a1408",
  fontWeight: 700, fontSize: 15, background: "transparent",
  textDecoration: "none", cursor: "pointer",
};

const IMG = {
  hero:     "https://images.unsplash.com/photo-1552664730-d307ca884978?w=1400&q=80&auto=format&fit=crop",
  inbox:    "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1200&q=80&auto=format&fit=crop",
  workflow: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&q=80&auto=format&fit=crop",
  team:     "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&q=80&auto=format&fit=crop",
};

const CAPS = [
  { icon: Inbox,       t: "One inbox, every channel",
    b: "SMS, email, WhatsApp, Facebook DMs, Instagram DMs, Google Business chat, HustleReviews responses, HustleEats offer-claims, HustlePlate bookings — every message lands in a single unified thread." },
  { icon: Bell,        t: "Auto-reply in seconds, not hours",
    b: "AI-drafted replies powered by your voice and your business context. Send with one tap, edit with two. Auto-send after office hours if you enable it." },
  { icon: Users,       t: "One customer, one card",
    b: "Every message, booking, review, offer-claim and payment for a given customer merges into one card. Never re-ask a question they already answered on a different channel." },
  { icon: Workflow,    t: "No-code automations",
    b: "\"When a review lands below 4 stars, DM the owner within 5 minutes and flag for follow-up.\" \"When a booking cancels, offer them a 10% credit for a re-book.\" Zero code — drop and go." },
  { icon: ShieldCheck, t: "Consent-first & data-portable",
    b: "Every customer's consent state (email / SMS / WhatsApp) is tracked per channel. Export the entire CRM to CSV any time, delete your account and take every byte with you." },
  { icon: TrendingUp,  t: "Live pipeline dashboards",
    b: "See lead volume, response times, conversion rate, revenue per staff member, and channel-mix in real time. Beats any spreadsheet you've built in the last decade." },
];

const CHAPTERS = [
  { kicker: "Chapter 1 · why every SMB needs a real CRM",
    title:  "You have 6 inboxes. Your customers have one.",
    body:   "Every Australian small business today runs on 6+ inboxes — Gmail for direct enquiries, WhatsApp for regulars, Instagram DMs for the youth crowd, Facebook Messenger for boomers, Google Business chat for near-me searches, plus SMS from the printed flyer. The customer doesn't care which channel they landed on — they just want an answer. But you're context-switching 40 times a day trying to remember which inbox has the latest thread. HustleCRM unifies every one of those channels into a single card per customer. Every message ever exchanged, on any channel, in one thread. Your reply goes back on whichever channel the customer used last. You'll respond twice as fast, twice as consistently, and nobody has to remember which app the last message came from.",
    img: IMG.inbox },
  { kicker: "Chapter 2 · the AI drafter that actually knows your business",
    title:  "Replies drafted in your voice, in your context.",
    body:   "Most CRM AI copilots write like corporate LinkedIn. HustleCRM's drafter is trained on your business context — your menu, your hours, your allergen policy, your booking rules — and speaks in the tone you set (formal / casual / matey / warm / precise). Drop in an incoming message; a drafted reply appears in ~1.2 seconds; you edit if you want, then hit send. If you enable Auto-Send After Hours, the drafter sends the reply automatically between 8pm and 8am so late-night enquiries don't sit unread until morning. Every draft that goes out is logged so you can audit what the AI sent on your behalf.",
    img: IMG.workflow },
  { kicker: "Chapter 3 · every module in the Suite plugs in",
    title:  "HustleEats, Reviews, Plate — all in one thread.",
    body:   "The single reason HustleCRM leaves other CRMs behind is that it sits on top of every HustleSuite module. A HustleReviews 4-star review lands as a thread in the same inbox as the HustlePlate booking that customer made 3 weeks ago and the HustleEats offer they claimed last Friday. You can literally scroll one customer's card and see: reviewed us 4 stars → booked last month → claimed an empty-seat offer last week → today asked about the Christmas menu. No CRM in Australia gives you this cross-module continuity, because no other CRM owns the underlying booking / review / offer modules.",
    img: IMG.team },
  { kicker: "Chapter 4 · automations that pay for themselves in week one",
    title:  "Set five rules. Recover thousands in month one.",
    body:   "The average HustleCRM customer sets five automations in their first fortnight: (1) any review under 4 stars → DM the owner within 5 minutes; (2) any booking cancellation → auto-send a 10% credit code for a re-book; (3) any first-time diner → 24-hour follow-up asking how the meal was; (4) any dormant customer (no interaction in 60 days) → weekly re-engagement email; (5) any HustleEats offer-claim → immediate personalised thank-you SMS. These five alone recover an average of $2,300 in month-one revenue for a mid-sized café. HustleCRM's automation builder is drop-and-go — no formulas, no Zapier connectors, no code.",
    img: IMG.hero },
  { kicker: "Chapter 5 · consent-first, portable, safe",
    title:  "The Australian data-privacy laws are on your side.",
    body:   "Every customer's consent for every channel (email / SMS / WhatsApp / marketing broadcasts) is tracked separately and timestamped. The unsubscribe from an email doesn't accidentally kill their SMS booking-confirmation. Full audit trail exportable to CSV any time — perfect for the Privacy Commissioner if you ever get audited. Delete your HustleCRM account and we hand back every customer card as a downloadable zip within 30 days. No data lock-in, no export tax. This is the CRM most Australian small businesses actually deserved a decade ago.",
    img: IMG.inbox },
  { kicker: "Chapter 6 · replace GoHighLevel + Zapier + Mailchimp",
    title:  "One monthly bill instead of five.",
    body:   "The average Australian café or trade business is currently paying $89/mo for a booking CRM, $59/mo for GoHighLevel (or Salesforce SB), $29/mo for Zapier, $49/mo for Mailchimp and $19/mo for WhatsApp Business API. That's $245/mo in tool-tax before you've earned a single dollar. HustleCRM's Featured tier at $79/mo replaces every one of them — booking sync, no-code automations, unified inbox, campaign broadcasts, WhatsApp API — and syncs cleanly with every HustleSuite module you're already paying for. On average our customers cut their SaaS spend by 60% in the first 90 days.",
    img: IMG.workflow },
];

const TIERS = [
  { key: "free", name: "Free forever", price: "$0", period: "/forever", color: "#1f7a1f",
    cta: { label: "Create free CRM account", href: "/claim-business?product=crm&tier=free" },
    blurb: "Every HustleSuite business gets a free HustleCRM account — 500 customer cards, 3 channels, and every core feature. Forever.",
    features: [
      "500 customer cards",
      "3 unified channels (email + 2 more)",
      "AI-drafted reply suggestions",
      "Basic pipeline dashboard",
      "CSV export any time",
      "Community support",
    ] },
  { key: "featured", name: "Featured", price: "$79", period: "/mo",
    badge: "Recommended", highlight: true, color: "#b08a3e",
    cta: { label: "Go Featured", href: "/claim-business?product=crm&tier=featured" },
    blurb: "Unlimited cards + every channel + auto-send + no-code automation builder. Replaces GoHighLevel, Zapier, Mailchimp and your booking CRM in one bill.",
    features: [
      "Everything in Free, plus:",
      "Unlimited customer cards",
      "Every channel: SMS, WhatsApp, IG, FB, GB",
      "Auto-send-after-hours reply drafter",
      "Unlimited no-code automations",
      "Campaign broadcasts (SMS + email + WA)",
      "Live pipeline + revenue dashboards",
      "Priority ticket support (24h SLA)",
    ] },
  { key: "agency", name: "Agency", price: "$249", period: "/mo", color: "#4a3b1f",
    cta: { label: "Book a discovery call", href: "/claim-business?product=crm&tier=agency" },
    blurb: "For agencies + multi-brand groups running CRM for 5+ client businesses. White-label admin, bulk operations, per-client billing.",
    features: [
      "Everything in Featured, plus:",
      "Manage up to 50 client accounts",
      "White-labelled admin branding",
      "Bulk campaign rollout across clients",
      "Per-client billing + payout schedules",
      "Dedicated account manager",
      "SOC 2 questionnaire pack",
    ] },
];

const FAQS = [
  { q: "Is HustleCRM actually free?",
    a: "Yes — 500 customer cards + 3 channels + every core feature forever. We fund the platform through the Featured tier and the wider HustleSuite ecosystem." },
  { q: "How's this different from GoHighLevel?",
    a: "Three ways. (1) It's fully Australian — GST-compliant billing, AU support hours, WhatsApp AU number pool. (2) It sits on top of every HustleSuite module — reviews, bookings, offers, ticket sales all cross-populate the same customer card. (3) It's priced honestly — $79/mo replaces the entire GoHighLevel + Zapier + Mailchimp stack." },
  { q: "Can I import my existing customer data?",
    a: "Yes. Bulk CSV upload, or direct import from Google Contacts, HubSpot, Mailchimp, Salesforce, and any modern CRM that exports CSV/vCard/JSON. Every imported record is timestamped and traceable." },
  { q: "Which channels are supported?",
    a: "Email (any IMAP/SMTP), SMS (Twilio + AU-compliant Route mobile pool), WhatsApp Business API, Instagram DMs, Facebook Messenger, Google Business chat, in-app HustleSuite messages, and HustleReviews public + private threads. Adding new channels as HustleSuite grows." },
  { q: "How does the AI-drafted reply work?",
    a: "Trained on your business context (menu, hours, policies, past replies). Suggests a reply in your voice; you tap-to-send or edit. Every draft is logged; every auto-sent reply is audit-trailed. You can turn the drafter off entirely." },
  { q: "What happens if I stop paying?",
    a: "You drop back to the free 500-card tier — all your existing customer cards stay live. Automations pause but data is preserved. Nothing is deleted. Delete your account and we hand back every byte within 30 days." },
];

function StatPill({ num, label }) {
  return (
    <div style={{ display:"inline-flex", alignItems:"baseline", gap:6,
                    padding:"8px 14px", borderRadius:999, background:"#fff",
                    border:"1px solid #ecdeb7", boxShadow:"0 4px 10px rgba(0,0,0,.05)" }}>
      <strong style={{fontFamily:"Georgia,serif", fontSize:20, color:"#1a1408"}}>{num}</strong>
      <span style={{fontSize:13, color:"#5a4a1a"}}>{label}</span>
    </div>
  );
}

function TierCard({ tier }) {
  return (
    <div data-testid={`crm-tier-${tier.key}`}
         style={{ background:"#fff", borderRadius:18, padding:"26px 24px",
                    border: tier.highlight ? "2px solid #b08a3e" : "1px solid #ecdeb7",
                    boxShadow: tier.highlight ? "0 20px 44px rgba(176,138,62,.22)" : "0 8px 20px rgba(0,0,0,.06)",
                    position:"relative", display:"flex", flexDirection:"column", gap:14 }}>
      {tier.badge && (
        <span style={{ position:"absolute", top:-12, right:18,
                         background:"linear-gradient(135deg,#1f1f1f 0%,#4a3b1f 50%,#b08a3e 100%)",
                         color:"#fff", fontSize:11, fontWeight:800,
                         padding:"5px 12px", borderRadius:999, letterSpacing:".05em" }}>{tier.badge}</span>
      )}
      <div>
        <div style={{color:tier.color, fontWeight:800, letterSpacing:".12em", fontSize:12, textTransform:"uppercase"}}>{tier.name}</div>
        <div style={{marginTop:6, fontFamily:"Georgia,serif", fontSize:36, color:"#1a1408"}}>
          {tier.price}<span style={{fontSize:15, color:"#8a7a4a", marginLeft:4}}>{tier.period}</span>
        </div>
      </div>
      <p style={{color:"#5a4a1a", lineHeight:1.55, fontSize:14, margin:0}}>{tier.blurb}</p>
      <ul style={{margin:0, padding:0, listStyle:"none", display:"grid", gap:8}}>
        {tier.features.map((f,i)=>(
          <li key={i} style={{display:"flex", gap:8, fontSize:14, color:"#1a1408", lineHeight:1.45}}>
            <Check size={16} color={tier.color} style={{flexShrink:0, marginTop:2}} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Link to={tier.cta.href} data-testid={`crm-tier-${tier.key}-cta`}
            style={{...ctaPrimary, alignSelf:"flex-start", marginTop:4,
                       background: tier.highlight ? "#b08a3e" : "#c2410c",
                       boxShadow: tier.highlight ? "0 8px 20px rgba(176,138,62,.32)" : "0 8px 20px rgba(194,65,12,.22)"}}>
        {tier.cta.label} <ArrowRight size={16} />
      </Link>
    </div>
  );
}

export default function HustleCRMSalesPage() {
  useEffect(() => { document.title = "HustleCRM — Every lead in one inbox."; }, []);
  return (
    <div data-testid="hustlecrm-sales-page" className="min-h-screen" style={{background:"#fdf9ec", color:"#1a1408"}}>
      <PlatformHeader salesTopGap
        brand={{ name: "HustleCRM", to: "/" }}
        nav={[
          { to: "/account/crm", label: "Open CRM" },
          { to: "#tiers", label: "Pricing" },
          { to: "/claim-business?product=crm", label: "Start free", align: "right", variant: "primary" },
        ]}
        showBookCta={false} showCart={false}
      />
      <main style={{maxWidth:1200, margin:"0 auto", padding:"24px 24px 80px"}}>
        <section data-testid="crm-hero" style={{padding:"40px 0 24px"}}>
          <img src={LOGO} alt="HustleCRM" style={{height:52, marginBottom:14}}/>
          <p style={{fontSize:12, letterSpacing:".22em", textTransform:"uppercase", color:"#b08a3e", fontWeight:800, margin:"0 0 10px"}}>HustleCRM · unified inbox</p>
          <h1 style={{fontFamily:"Georgia,serif", fontSize:46, lineHeight:1.1, color:"#1a1408", margin:"0 0 14px", maxWidth:900}}>
            Every lead in <em style={{color:"#b08a3e", fontStyle:"italic"}}>one inbox.</em>
          </h1>
          <p style={{fontSize:17, color:"#5a4a1a", maxWidth:820, margin:"0 0 20px", lineHeight:1.55}}>
            HustleCRM is the unified CRM your business always needed. SMS, email, WhatsApp, Instagram DMs, Facebook Messenger, Google Business chat, HustleReviews responses and HustlePlate bookings — every message, every customer, one thread. AI-drafted replies in your voice. No-code automations. Free forever for 500 cards.
          </p>
          <div style={{display:"flex", gap:10, flexWrap:"wrap", marginBottom:22}}>
            <StatPill num="8+" label="channels unified"/>
            <StatPill num="~1.2s" label="AI-drafted reply time"/>
            <StatPill num="$79" label="/mo replaces 5 SaaS tools"/>
            <StatPill num="Free" label="500 cards forever"/>
          </div>
          <div style={{display:"flex", gap:12, flexWrap:"wrap"}}>
            <Link to="/claim-business?product=crm" data-testid="crm-cta-start" style={ctaPrimary}>Start free <ArrowRight size={16}/></Link>
            <Link to="/account/crm" data-testid="crm-cta-open" style={ctaSecondary}><Inbox size={16}/>Open my CRM</Link>
            <button data-testid="crm-print-btn" onClick={()=>window.print()} style={{...ctaSecondary, cursor:"pointer"}}><Printer size={16}/>Print proposal</button>
          </div>
          <img src={IMG.hero} alt="Unified customer inbox on HustleCRM" style={{marginTop:32, width:"100%", height:340, objectFit:"cover", borderRadius:18, border:"1px solid #ecdeb7", boxShadow:"0 20px 44px rgba(0,0,0,.08)"}}/>
        </section>

        <section style={{marginTop:64}} data-testid="crm-caps">
          <p style={{fontSize:12, letterSpacing:".22em", textTransform:"uppercase", color:"#b08a3e", fontWeight:800, margin:"0 0 8px"}}>Chapter 0 · what ships in the box</p>
          <h2 style={{fontFamily:"Georgia,serif", fontSize:34, color:"#1a1408", margin:"0 0 24px"}}>Six capabilities that unify your customer view.</h2>
          <div style={{display:"grid", gap:20, gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))"}}>
            {CAPS.map((c,i)=>(
              <div key={i} data-testid={`crm-cap-${i}`} style={{background:"#fff", borderRadius:16, padding:"22px 22px", border:"1px solid #ecdeb7", boxShadow:"0 8px 20px rgba(0,0,0,.05)"}}>
                <div style={{width:40, height:40, borderRadius:10, background:"#fff8e6", color:"#b08a3e", display:"grid", placeItems:"center", marginBottom:12}}>
                  <c.icon size={22}/>
                </div>
                <h3 style={{fontFamily:"Georgia,serif", fontSize:18, color:"#1a1408", margin:"0 0 6px"}}>{c.t}</h3>
                <p style={{fontSize:14, color:"#5a4a1a", lineHeight:1.5, margin:0}}>{c.b}</p>
              </div>
            ))}
          </div>
        </section>

        <section style={{marginTop:72}} data-testid="crm-chapters">
          {CHAPTERS.map((ch,i)=>(
            <article key={i} data-testid={`crm-chapter-${i}`} style={{display:"grid", gridTemplateColumns: i%2 ? "1fr 320px" : "320px 1fr", gap:32, alignItems:"center", marginBottom:56}}>
              <img src={ch.img} alt={ch.title} style={{width:"100%", height:240, objectFit:"cover", borderRadius:14, border:"1px solid #ecdeb7", order: i%2 ? 2 : 1}}/>
              <div style={{order: i%2 ? 1 : 2}}>
                <p style={{fontSize:12, letterSpacing:".22em", textTransform:"uppercase", color:"#b08a3e", fontWeight:800, margin:"0 0 8px"}}>{ch.kicker}</p>
                <h3 style={{fontFamily:"Georgia,serif", fontSize:28, color:"#1a1408", margin:"0 0 12px", lineHeight:1.2}}>{ch.title}</h3>
                <p style={{fontSize:15, color:"#5a4a1a", lineHeight:1.7, margin:0}}>{ch.body}</p>
              </div>
            </article>
          ))}
        </section>

        <section id="tiers" style={{marginTop:72}} data-testid="crm-tiers">
          <p style={{fontSize:12, letterSpacing:".22em", textTransform:"uppercase", color:"#b08a3e", fontWeight:800, margin:"0 0 8px"}}>Chapter 7 · pricing</p>
          <h2 style={{fontFamily:"Georgia,serif", fontSize:34, color:"#1a1408", margin:"0 0 10px"}}>Free forever. Featured when you scale.</h2>
          <p style={{fontSize:15, color:"#5a4a1a", maxWidth:820, lineHeight:1.55, margin:"0 0 28px"}}>Every HustleSuite business gets a free HustleCRM account. Featured unlocks unlimited cards + every channel + no-code automations. Agency tier for studios running CRM across 5+ client businesses.</p>
          <div style={{display:"grid", gap:20, gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))"}}>
            {TIERS.map(t=><TierCard key={t.key} tier={t}/>)}
          </div>
        </section>

        <section style={{marginTop:72}} data-testid="crm-faq">
          <p style={{fontSize:12, letterSpacing:".22em", textTransform:"uppercase", color:"#b08a3e", fontWeight:800, margin:"0 0 8px"}}>Chapter 8 · honest FAQ</p>
          <h2 style={{fontFamily:"Georgia,serif", fontSize:34, color:"#1a1408", margin:"0 0 24px"}}>Questions business owners actually ask.</h2>
          <div style={{display:"grid", gap:12}}>
            {FAQS.map((f,i)=>(
              <details key={i} data-testid={`crm-faq-${i}`} style={{background:"#fff", borderRadius:12, border:"1px solid #ecdeb7", padding:"16px 20px"}}>
                <summary style={{fontFamily:"Georgia,serif", fontSize:17, color:"#1a1408", cursor:"pointer", fontWeight:600}}>{f.q}</summary>
                <p style={{fontSize:14, color:"#5a4a1a", lineHeight:1.55, margin:"10px 0 0"}}>{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section style={{marginTop:72}} data-testid="crm-final-cta">
          <div style={{background:"linear-gradient(135deg,#1a1408 0%,#4a3b1f 55%,#b08a3e 100%)", color:"#fff", padding:"40px 36px", borderRadius:22, display:"flex", flexWrap:"wrap", gap:24, alignItems:"center"}}>
            <div style={{flex:1, minWidth:260}}>
              <h2 style={{fontFamily:"Georgia,serif", fontSize:30, margin:"0 0 8px"}}>Every lead. One inbox. Zero excuses.</h2>
              <p style={{fontSize:15, opacity:.9, margin:0, lineHeight:1.55}}>Two minutes to set up. Zero card. Free forever for 500 cards. If you don't recover the Featured tier's cost in your first month, walk away — you keep every customer card.</p>
            </div>
            <div style={{display:"flex", gap:12, flexWrap:"wrap"}}>
              <Link to="/claim-business?product=crm" data-testid="crm-final-start" style={{...ctaPrimary, background:"#fff", color:"#1a1408", boxShadow:"0 10px 30px rgba(0,0,0,.28)"}}>Start free <ArrowRight size={16}/></Link>
              <Link to="/claim-business?product=crm&tier=featured" data-testid="crm-final-featured" style={{...ctaSecondary, borderColor:"#fff", color:"#fff"}}><Building2 size={16}/>Talk about Featured</Link>
            </div>
          </div>
        </section>
      </main>
      <FounderChatPill testid="crm-founder-chat" />
      <SuiteFooter />
    </div>
  );
}
